import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { waitlistApi } from '../services/api';

const WaitlistPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profession: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formMsg, setFormMsg] = useState('');
  const [successData, setSuccessData] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
    setFormMsg('');
  };

  const validate = () => {
    const errs = {};
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const profession = formData.profession.trim();
    const digits = phone.replace(/\D/g, '');

    if (name.length < 2) errs.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errs.email = 'Please enter your professional email.';
    if (phone && (digits.length < 7 || digits.length > 15)) {
      errs.phone = 'Please enter a valid phone number (7 to 15 digits).';
    }
    if (!profession || profession.length < 2 || profession === 'Select your profession') {
      errs.profession = 'Please select your profession.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMsg('');
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await waitlistApi.joinWaitlist({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        profession: formData.profession.trim()
      });

      if (res && res.success) {
        setSuccessData({
          firstName: formData.name.trim().split(/\s+/)[0],
          email: formData.email.trim(),
          position: res.position || null
        });
      } else {
        setFormMsg(res?.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      setFormMsg(err?.message || 'Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="psychmitra-waitlist-root">
      <style>{`
        html, body {
          overflow-x: hidden;
        }

        .psychmitra-waitlist-root {
          --brand-purple: #6a3bf0;
          --brand-pink: #db2777;
          --font: "Poppins", sans-serif;
          
          min-height: 100vh;
          background: radial-gradient(1200px 650px at 50% 0%, #ede9fe 0%, #fdf2f8 35%, #f8fafc 100%);
          color: #0f172a;
          font-family: var(--font);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: visible;
        }

        /* Ambient colored lighting */
        .ambient-glow-center {
          position: absolute;
          width: 600px;
          height: 600px;
          top: 10%;
          left: 50%;
          transform: translateX(-50%);
          background: radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(236, 72, 153, 0.07) 50%, transparent 70%);
          pointer-events: none;
          z-index: 0;
          filter: blur(40px);
        }

        /* Top Header Bar with Centered Logo */
        .waitlist-header-bar {
          position: relative;
          z-index: 30;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 24px 20px 10px;
          max-width: 1280px;
          width: 100%;
          margin: 0 auto;
        }
        .waitlist-logo-wrap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: transform 0.2s ease;
        }
        .waitlist-logo-wrap:hover {
          transform: translateY(-2px);
        }
        .waitlist-logo-img {
          height: 90px;
          width: auto;
          object-fit: contain;
          filter: drop-shadow(0 3px 12px rgba(106, 59, 240, 0.18));
        }

        /* Main Hero Stage - Scrollable with generous breathing space */
        .waitlist-stage {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 200px 16px 240px;
          position: relative;
          min-height: auto;
        }

        @media (max-width: 1024px) {
          .waitlist-stage {
            padding: 60px 16px 320px;
          }
        }
        @media (max-width: 768px) {
          .waitlist-stage {
            padding: 30px 16px 40px;
          }
        }

        /* ========================================================
           PROPER CIRCULAR CONCENTRIC RINGS AROUND CONTENT & FORM
           ======================================================== */
        .rings-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 1000px;
          height: 1000px;
          pointer-events: none;
          z-index: 25;
        }

        .orbit-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          z-index: 1;
        }

        /* Proper circular concentric rings enclosing both the content and form */
        .orbit-ring.ring-1 {
          width: 740px;
          height: 740px;
          border: 1.5px dashed rgba(147, 51, 234, 0.28);
          box-shadow: 0 0 25px rgba(147, 51, 234, 0.04);
        }
        .orbit-ring.ring-2 {
          width: 900px;
          height: 900px;
          border: 1.5px solid rgba(168, 85, 247, 0.40);
          box-shadow: 0 0 30px rgba(168, 85, 247, 0.08);
        }
        .orbit-ring.ring-3 {
          width: 1060px;
          height: 1060px;
          border: 1.5px dashed rgba(236, 72, 153, 0.36);
          box-shadow: 0 0 35px rgba(236, 72, 153, 0.06);
        }

        /* ========================================================
           ORBITAL ANIMATIONS FOR RING 2 (radius 430px) & RING 3 (radius 500px)
           Uses pure GPU transforms: rotate(θ) translateY(-R) rotate(-θ)
           Guarantees 100% upright pills (no self-rotation) at exact ring radius
           ======================================================== */
        /* Ring 2 Orbit (radius 440px, 3 pills spaced by 120deg) */
        @keyframes orbitRing2_1 {
          0% { transform: translate(-50%, -50%) rotate(0deg) translateY(-440px) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg) translateY(-440px) rotate(-360deg); }
        }
        @keyframes orbitRing2_2 {
          0% { transform: translate(-50%, -50%) rotate(120deg) translateY(-440px) rotate(-120deg); }
          100% { transform: translate(-50%, -50%) rotate(480deg) translateY(-440px) rotate(-480deg); }
        }
        @keyframes orbitRing2_3 {
          0% { transform: translate(-50%, -50%) rotate(240deg) translateY(-440px) rotate(-240deg); }
          100% { transform: translate(-50%, -50%) rotate(600deg) translateY(-440px) rotate(-600deg); }
        }

        /* Ring 3 Orbit (radius 510px, 2 pills spaced by 180deg) */
        @keyframes orbitRing3_1 {
          0% { transform: translate(-50%, -50%) rotate(60deg) translateY(-510px) rotate(-60deg); }
          100% { transform: translate(-50%, -50%) rotate(420deg) translateY(-510px) rotate(-420deg); }
        }
        @keyframes orbitRing3_2 {
          0% { transform: translate(-50%, -50%) rotate(240deg) translateY(-510px) rotate(-240deg); }
          100% { transform: translate(-50%, -50%) rotate(600deg) translateY(-510px) rotate(-600deg); }
        }
        @keyframes orbitRing3_3 {
          0% { transform: translate(-50%, -50%) rotate(360deg) translateY(-510px) rotate(-360deg); }
          100% { transform: translate(-50%, -50%) rotate(720deg) translateY(-510px) rotate(-720deg); }
        }

        /* Base Floating Pill Slot */
        .floating-pill-slot {
          position: absolute;
          top: 50%;
          left: 50%;
          pointer-events: auto;
          z-index: 30; /* Always in front and visible all around */
          will-change: transform;
        }

        .floating-pill-slot:hover {
          animation-play-state: paused;
        }

        /* Ring 2 Pills (36s orbit, radius 430px) */
        .floating-pill-slot.slot-r2-1 {
          animation: orbitRing2_1 36s linear infinite;
        }
        .floating-pill-slot.slot-r2-2 {
          animation: orbitRing2_2 36s linear infinite;
        }
        .floating-pill-slot.slot-r2-3 {
          animation: orbitRing2_3 36s linear infinite;
        }

        /* Ring 3 Pills (44s orbit, radius 500px) */
        .floating-pill-slot.slot-r3-1 {
          animation: orbitRing3_1 44s linear infinite;
        }
        .floating-pill-slot.slot-r3-2 {
          animation: orbitRing3_2 44s linear infinite;
        }
        .floating-pill-slot.slot-r3-3 {
          animation: orbitRing3_3 44s linear infinite; 
        }

        /* Floating Pill Chip (Stationary, upright, interactive on hover) */
        .orbit-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.95);
          border: 1.5px solid rgba(255, 255, 255, 0.98);
          border-radius: 999px;
          padding: 6px 14px;
          box-shadow: 0 6px 20px rgba(106, 59, 240, 0.12), 0 2px 5px rgba(0,0,0,0.03);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          font-size: 12.5px;
          font-weight: 700;
          color: #1e293b;
          white-space: nowrap;
          cursor: default;
          user-select: none;
          transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
        }
        .orbit-chip:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 25px rgba(106, 59, 240, 0.2);
        }

        .orbit-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .orbit-icon {
          font-size: 13px;
          line-height: 1;
        }

        .orbit-chip.purple .orbit-dot { background: #7c3aed; box-shadow: 0 0 7px #7c3aed; }
        .orbit-chip.pink .orbit-dot { background: #db2777; box-shadow: 0 0 7px #db2777; }
        .orbit-chip.blue .orbit-dot { background: #0284c7; box-shadow: 0 0 7px #0284c7; }
        .orbit-chip.amber .orbit-dot { background: #d97706; box-shadow: 0 0 7px #d97706; }
        .orbit-chip.emerald .orbit-dot { background: #059669; box-shadow: 0 0 7px #059669; }
        .orbit-chip.yellow .orbit-dot { background: #f59e0b; box-shadow: 0 0 7px #f59e0b; }

        /* Luminous decorative orbs on rings */
        .orbit-orb {
          position: absolute;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ffffff, #c4b5fd);
          box-shadow: 0 0 10px rgba(167, 139, 250, 0.9), 0 0 20px rgba(236, 72, 153, 0.5);
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .orbit-orb.small {
          width: 6px;
          height: 6px;
        }

        /* Responsive scaling so rings and pills fit viewport without cropping */
        @media (max-height: 780px) {
          .rings-container {
            transform: translate(-50%, -50%) scale(0.88);
          }
        }
        @media (max-height: 680px) {
          .rings-container {
            transform: translate(-50%, -50%) scale(0.78);
          }
        }
        @media (max-width: 1024px) {
          .rings-container {
            transform: translate(-50%, -50%) scale(0.85);
          }
        }
        @media (max-width: 768px) {
          .rings-container {
            display: none;
          }
          .features-pill-row {
            display: flex !important;
          }
        }

        /* Hero Content */
        .waitlist-hero-content {
          width: 100%;
          max-width: 580px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 4px;
          position: relative;
          z-index: 10;
        }

        /* Eyebrow Tagline */
        .waitlist-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.95);
          border: 1.5px solid #e0e7ff;
          padding: 3px 13px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          color: var(--brand-purple);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          box-shadow: 0 3px 12px rgba(106, 59, 240, 0.08);
          margin-bottom: 2px;
        }
        .waitlist-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #db2777;
          box-shadow: 0 0 6px #db2777;
          animation: waitlistDotPulse 2s infinite ease-in-out;
        }
        @keyframes waitlistDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(0.65); opacity: 0.5; }
        }

        /* Headline */
        .waitlist-main-heading {
          margin: 0;
          display: block;
          text-align: center;
          font-size: clamp(1.45rem, 2.3vw, 1.95rem);
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.2;
          color: #0f172a;
          max-width: 24ch;
        }
        .waitlist-grad-word {
          background: linear-gradient(135deg, #6a3bf0 0%, #db2777 80%, #ea580c 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: inline-block;
          font-weight: 900;
        }

        /* Description */
        .waitlist-lede-text {
          margin: 0;
          font-size: 12.5px;
          line-height: 1.45;
          color: #475569;
          font-weight: 500;
          max-width: 75ch;
          text-align: center;
        }

        /* Floating words / features pill row (shown on mobile when rings hide) */
        .features-pill-row {
          display: none;
          align-items: center;
          justify-content: center;
          gap: 6px;
          flex-wrap: wrap;
          margin: 6px 0 2px;
        }
        .feature-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(226, 232, 240, 0.9);
          padding: 5px 12px;
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 700;
          color: #334155;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
          transition: all 0.2s ease;
          user-select: none;
        }
        .feature-chip-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .feature-chip-icon {
          font-size: 13px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
        }
        .feature-chip.purple .feature-chip-dot { background: #7c3aed; }
        .feature-chip.pink .feature-chip-dot { background: #db2777; }
        .feature-chip.blue .feature-chip-dot { background: #0284c7; }
        .feature-chip.amber .feature-chip-dot { background: #d97706; }
        .feature-chip.emerald .feature-chip-dot { background: #059669; }
        .feature-chip.yellow .feature-chip-dot { background: #f59e0b; }

        /* Waitlist Form Card Wrapper */
        .waitlist-card-wrapper {
          position: relative;
          width: 100%;
          max-width: 490px;
          display: flex;
          justify-content: center;
          margin-top: 6px;
        }

        .waitlist-card {
          width: 100%;
          background: rgba(255, 255, 255, 0.94);
          border: 1.5px solid rgba(255, 255, 255, 0.98);
          border-radius: 20px;
          padding: 16px 22px;
          box-shadow: 0 16px 40px rgba(106, 59, 240, 0.08), 0 2px 6px rgba(0,0,0,0.02);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          text-align: left;
          position: relative;
          z-index: 10;
        }

        .waitlist-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f5f9;
        }
        .waitlist-card-title {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
        }
        .waitlist-card-subtitle {
          margin: 2px 0 0;
          font-size: 11.5px;
          color: #64748b;
          font-weight: 500;
        }
        .waitlist-card-badge {
          font-size: 10px;
          font-weight: 800;
          padding: 3px 9px;
          border-radius: 20px;
          background: rgba(106, 59, 240, 0.1);
          color: var(--brand-purple);
          letter-spacing: 0.02em;
          white-space: nowrap;
        }

        /* Form Grid */
        .waitlist-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        @media (max-width: 560px) {
          .waitlist-form-grid {
            grid-template-columns: 1fr;
          }
          .waitlist-card {
            padding: 16px 16px;
            border-radius: 18px;
          }
        }

        .waitlist-field {
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 6px 10px;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          transition: all 0.2s ease;
        }
        .waitlist-field:focus-within {
          border-color: var(--brand-purple);
          box-shadow: 0 0 0 3px rgba(106, 59, 240, 0.12);
          background: #ffffff;
        }
        .waitlist-field.has-error {
          border-color: #ef4444;
          background: #fff5f5;
        }

        .waitlist-field-icon {
          width: 24px;
          height: 24px;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f3ff;
          color: var(--brand-purple);
          flex-shrink: 0;
          margin-top: 1px;
          transition: all 0.2s ease;
        }
        .waitlist-field:focus-within .waitlist-field-icon {
          background: var(--brand-purple);
          color: #ffffff;
        }

        .waitlist-field-body {
          flex: 1;
          min-width: 0;
        }
        .waitlist-field-label {
          display: block;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 1px;
        }
        .waitlist-field-input, .waitlist-field-select {
          width: 100%;
          border: 0;
          outline: 0;
          background: transparent;
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 600;
          color: #0f172a;
          padding: 0;
        }
        .waitlist-field-input::placeholder {
          color: #94a3b8;
          font-weight: 400;
          font-size: 12px;
        }
        .waitlist-field-select {
          cursor: pointer;
          -webkit-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236a3bf0' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0 center;
          padding-right: 18px;
        }
        .waitlist-field-select option {
          color: #0f172a;
          background: #ffffff;
          font-weight: 500;
        }

        .waitlist-errors-banner {
          grid-column: 1 / -1;
          margin: 2px 0 0 2px;
          font-size: 11px;
          font-weight: 600;
          color: #dc2626;
        }

        /* Submit Button */
        .waitlist-submit-btn {
          margin-top: 10px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 0;
          cursor: pointer;
          background: linear-gradient(135deg, #18181b 0%, #27272a 100%);
          color: #ffffff;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.05em;
          padding: 10px 22px;
          border-radius: 999px;
          box-shadow: 0 6px 18px rgba(24, 24, 27, 0.2);
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .waitlist-submit-btn:hover {
          transform: translateY(-2px);
          background: linear-gradient(135deg, #6a3bf0 0%, #db2777 100%);
          box-shadow: 0 10px 24px rgba(106, 59, 240, 0.28);
        }
        .waitlist-submit-btn:active {
          transform: translateY(0);
        }
        .waitlist-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .waitlist-form-msg {
          margin: 6px 0 0;
          font-size: 11.5px;
          font-weight: 600;
          color: #dc2626;
          text-align: center;
        }

        /* Success Confirmation */
        .waitlist-done-box {
          padding: 16px 12px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .waitlist-done-tick {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: #ffffff;
          background: linear-gradient(135deg, #6a3bf0 0%, #db2777 100%);
          box-shadow: 0 0 0 6px rgba(106, 59, 240, 0.12);
        }
        .waitlist-done-title {
          margin: 2px 0 0;
          font-size: 1.25rem;
          font-weight: 800;
          color: #0f172a;
        }
        .waitlist-done-text {
          margin: 0;
          color: #475569;
          font-size: 13px;
          line-height: 1.45;
        }
        .waitlist-done-pos {
          font-size: 12px;
          font-weight: 700;
          color: var(--brand-purple);
          background: #f5f3ff;
          padding: 3px 12px;
          border-radius: 999px;
          margin-top: 2px;
        }

        /* Footer Note */
        .waitlist-footer-note {
          margin: 8px 0 0;
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          text-align: center;
          line-height: 1.4;
          max-width: 48ch;
        }
      `}</style>

      {/* Decorative ambient background glow */}
      <div className="ambient-glow-center"></div>

      {/* Top Header */}
      <header className="waitlist-header-bar">
        <Link to="#" className="waitlist-logo-wrap" title="PsychMitra Home">
          <img
            src="/assets/images/PsychMitra.png"
            alt="PsychMitra Logo"
            className="waitlist-logo-img"
          />
        </Link>
      </header>

      {/* Main Content Stage */}
      <main className="waitlist-stage">
        {/* Central Hero Content */}
        <section className="waitlist-hero-content">
          {/* ========================================================
              PROPER CIRCULAR CONCENTRIC RINGS AROUND CONTENT & FORM
              ======================================================== */}
          <div className="rings-container" aria-hidden="true">
            {/* Concentric Visible Rings */}
            <div className="orbit-ring ring-1"></div>
            <div className="orbit-ring ring-2"></div>
            <div className="orbit-ring ring-3"></div>

            {/* Decorative Subtle Glowing Orbs on the rings */}
            <span className="orbit-orb" style={{ top: '15%', left: '78%' }}></span>
            <span className="orbit-orb small" style={{ top: '85%', left: '22%' }}></span>
            <span className="orbit-orb small" style={{ top: '30%', left: '12%' }}></span>
            <span className="orbit-orb" style={{ top: '70%', left: '88%' }}></span>

            {/* Orbital Floating Pills revolving around orbit-ring.ring-2 and orbit-ring.ring-3 */}
            {/* Ring 2 Orbit (860px diameter, radius 430px) */}
            <div className="floating-pill-slot slot-r2-1">
              <div className="orbit-chip purple">
                {/* <span className="orbit-dot"></span> */}
                <span className="orbit-icon">👁️</span>
                <span>Be visible</span>
              </div>
            </div>

            <div className="floating-pill-slot slot-r2-2">
              <div className="orbit-chip blue">
                {/* <span className="orbit-dot"></span>
                <span className="orbit-icon">⚡</span>
                <span>Automation</span>
              </div>
            </div>

            <div className="floating-pill-slot slot-r2-3">
              <div className="orbit-chip amber">
                {/* <span className="orbit-dot"></span> */}
                <span className="orbit-icon">📋</span>
                <span>Worksheets</span>
              </div>
            </div>

            {/* Ring 3 Orbit (1000px diameter, radius 500px) */}
            <div className="floating-pill-slot slot-r3-1">
              <div className="orbit-chip pink">
                {/* <span className="orbit-dot"></span> */}
                <span className="orbit-icon">🛍️</span>
                <span>e-products</span>
              </div>
            </div>

            <div className="floating-pill-slot slot-r3-2">
              <div className="orbit-chip emerald">
                {/* <span className="orbit-dot"></span> */}
                <span className="orbit-icon">✍️</span>
                <span>Blogs</span>
              </div>
            </div>
            <div className="floating-pill-slot slot-r3-3">
              <div className="orbit-chip yellow">
                {/* <span className="orbit-dot"></span> */}
                <span className="orbit-icon">🎭</span>
                <span>Workshops</span>
              </div>
            </div>
          </div>

          {/* Eyebrow */}
          <div className="waitlist-eyebrow">
            <span className="waitlist-eyebrow-dot"></span>
            <span>0% commission. Built for MHPs.</span>
          </div>

          {/* Main Title */}
          <h1 className="waitlist-main-heading">
            Give your expertise the space it needs to <span className="waitlist-grad-word">grow</span>
          </h1>

          {/* Description */}
          <p className="waitlist-lede-text">
            PsychMitra is creating a platform to help mental health professionals grow their visibility, expand their impact and stay connected with clients beyond the session.
          </p>

          {/* Mobile-only features row (visible on screens <= 768px where rings are hidden) */}
          <div className="features-pill-row">
            <div className="feature-chip purple">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">👁️</span>
              <span>Be visible</span>
            </div>
            <div className="feature-chip blue">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">⚡</span>
              <span>Automation</span>
            </div>
            <div className="feature-chip amber">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">📋</span>
              <span>Worksheets</span>
            </div>
            <div className="feature-chip pink">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">🛍️</span>
              <span>e-products</span>
            </div>
            <div className="feature-chip emerald">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">✍️</span>
              <span>Blogs</span>
            </div>
            <div className="feature-chip yellow">
              {/* <span className="feature-chip-dot"></span> */}
              <span className="feature-chip-icon">🎭</span>
              <span>Workshops</span>
            </div>
          </div>

          {/* Form Card */}
          <div className="waitlist-card">
            {!successData ? (
              <form onSubmit={handleSubmit} noValidate>
                <div className="waitlist-card-header">
                  <div>
                    <h2 className="waitlist-card-title">Join the waitlist</h2>
                    <p className="waitlist-card-subtitle">Reserve your priority spot for early access before launch.</p>
                  </div>
                  {/* <span className="waitlist-card-badge">0% Commission</span> */}
                </div>

                <div className="waitlist-form-grid">
                  {/* Field 1: Name */}
                  <div className={`waitlist-field ${errors.name ? 'has-error' : ''}`}>
                    <div className="waitlist-field-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M6 20v-2a6 6 0 0 1 12 0v2" />
                      </svg>
                    </div>
                    <div className="waitlist-field-body">
                      <label htmlFor="name-input" className="waitlist-field-label">Name</label>
                      <input
                        id="name-input"
                        type="text"
                        className="waitlist-field-input"
                        autoComplete="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Field 2: Profession */}
                  <div className={`waitlist-field ${errors.profession ? 'has-error' : ''}`}>
                    <div className="waitlist-field-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>
                    <div className="waitlist-field-body">
                      <label htmlFor="prof-input" className="waitlist-field-label">Profession</label>
                      <select
                        id="prof-input"
                        className="waitlist-field-select"
                        value={formData.profession}
                        onChange={(e) => handleChange('profession', e.target.value)}
                        required
                        style={{ color: formData.profession ? '#0f172a' : '#94a3b8' }}
                      >
                        <option value="" disabled style={{ color: '#94a3b8' }}>Select your profession</option>
                        <option value="Clinical Psychologist">Clinical Psychologist</option>
                        <option value="Counselling Psychologist">Counselling Psychologist</option>
                        <option value="Psychiatrist">Psychiatrist</option>
                        <option value="Psychotherapist">Psychotherapist</option>
                        <option value="Mental Health Counselor">Mental Health Counselor</option>
                        <option value="Child & Adolescent Counselor">Child & Adolescent Counselor</option>
                        <option value="Couples & Family Therapist">Couples & Family Therapist</option>
                        <option value="Clinical Social Worker">Clinical Social Worker</option>
                        <option value="Expressive Arts / Music Therapist">Expressive Arts / Music Therapist</option>
                        <option value="Psychology Trainee / Student">Psychology Trainee / Student</option>
                        <option value="Other Mental Health Professional">Other Mental Health Professional</option>
                      </select>
                    </div>
                  </div>

                  {/* Field 3: Email */}
                  <div className={`waitlist-field ${errors.email ? 'has-error' : ''}`}>
                    <div className="waitlist-field-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <polyline points="3 7 12 13 21 7" />
                      </svg>
                    </div>
                    <div className="waitlist-field-body">
                      <label htmlFor="email-input" className="waitlist-field-label">Email</label>
                      <input
                        id="email-input"
                        type="email"
                        className="waitlist-field-input"
                        autoComplete="email"
                        placeholder="Your professional email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Field 4: Phone number (optional) */}
                  <div className={`waitlist-field ${errors.phone ? 'has-error' : ''}`}>
                    <div className="waitlist-field-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div className="waitlist-field-body">
                      <label htmlFor="phone-input" className="waitlist-field-label">Phone number (optional)</label>
                      <input
                        id="phone-input"
                        type="tel"
                        className="waitlist-field-input"
                        autoComplete="tel"
                        placeholder="Add your number for faster access updates."
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                      />
                    </div>
                  </div>

                  {Object.keys(errors).length > 0 && (
                    <div className="waitlist-errors-banner">
                      {Object.values(errors).join(' ')}
                    </div>
                  )}
                </div>

                <button className="waitlist-submit-btn" type="submit" disabled={loading}>
                  <span>{loading ? 'RESERVING YOUR SPOT…' : 'RESERVE MY SPOT →'}</span>
                </button>
                {formMsg && <p className="waitlist-form-msg">{formMsg}</p>}
              </form>
            ) : (
              <div className="waitlist-done-box" role="status">
                <div className="waitlist-done-tick">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="waitlist-done-title">You’re on the list, {successData.firstName}!</h2>
                <p className="waitlist-done-text">We’ll email <b>{successData.email}</b> when your priority spot opens.</p>
                {/* {successData.position && (
                  <div className="waitlist-done-pos">
                    Waitlist Position: #{successData.position}
                  </div>
                )} */}
              </div>
            )}
          </div>

          {/* Footer Subtext */}
          <p className="waitlist-footer-note">
            Built by Mental Health Professionals for Mental Health Professionals and everyone seeking support.
          </p>
        </section>
      </main>
    </div>
  );
};

export default WaitlistPage;
